/**
 * `CrsSettings` — GVW-149 default CRS / units.
 *
 * Per brief §2 / §5: sets default CRS (EPSG/MGRS/UTM/local-grid) and units
 * (metric / imperial) for the user. Persists via localStorage fallback under
 * `gvw-set-crs` per dispatch §1 (platform-sdk lacks a per-user CRS setting
 * endpoint in the stub).
 *
 * TODO(WP-E11): when `@geosoft/platform-sdk` exposes a real user-prefs API,
 * swap `localStorage` for `sdk.userPrefs.set('crs', ...)` here.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-149/brief.md
 */

import { useCallback, useEffect, useState } from 'react';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

interface CrsDefaults {
  crsCode: string;
  units: 'metric' | 'imperial';
}

const STORAGE_KEY = 'gvw-set-crs';
const DEFAULTS: CrsDefaults = { crsCode: 'EPSG:4326', units: 'metric' };

const COMMON_CRS = [
  { code: 'EPSG:4326', name: 'WGS 84 (geographic)' },
  { code: 'EPSG:3857', name: 'Web Mercator' },
  { code: 'EPSG:4269', name: 'NAD83 (geographic)' },
  { code: 'EPSG:32610', name: 'UTM Zone 10N (WGS 84)' },
  { code: 'EPSG:32633', name: 'UTM Zone 33N (WGS 84)' },
];

export function CrsSettings() {
  const liveRegion = useLiveRegion();
  const [settings, setSettings] = useState<CrsDefaults>(DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) setSettings(JSON.parse(stored) as CrsDefaults);
    } catch {
      /* defaults */
    }
  }, []);

  useEffect(() => {
    document.title = 'Default CRS · GeoViewer';
    liveRegion.announce('status', 'Default CRS settings loaded.');
  }, [liveRegion]);

  const handleSave = useCallback(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      setSaved(true);
      liveRegion.announce('status', `Default CRS saved: ${settings.crsCode}, ${settings.units}.`);
    } catch {
      liveRegion.announce('alert', 'Could not save CRS settings.');
    }
  }, [settings, liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="gvw-149-heading"
      data-gvw-id="GVW-149"
      data-route="/workspace/settings/crs"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-149-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          language
        </span>
        Default CRS / units
      </h1>

      {saved ? (
        <div
          role="status"
          style={{
            padding: tokens.space[3],
            marginBottom: tokens.space[4],
            background: tokens.color.surface[1],
            borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.success}`,
            borderRadius: tokens.radius.md,
          }}
        >
          Default CRS saved.
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[5], maxWidth: 560 }}
      >
        <div>
          <label
            htmlFor="gvw-149-crs"
            style={{ display: 'block', fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, marginBottom: tokens.space[2] }}
          >
            Coordinate reference system
          </label>
          <select
            id="gvw-149-crs"
            value={settings.crsCode}
            onChange={(e) => setSettings((p) => ({ ...p, crsCode: e.target.value }))}
            style={{
              width: '100%',
              padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: tokens.border.thin,
              borderRadius: tokens.radius.input,
              fontSize: tokens.font.size.md,
            }}
          >
            {COMMON_CRS.map((crs) => (
              <option key={crs.code} value={crs.code}>{crs.code} — {crs.name}</option>
            ))}
          </select>
        </div>

        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend
            style={{
              fontSize: tokens.font.size.lg,
              fontWeight: tokens.font.weight.medium,
              marginBottom: tokens.space[2],
              display: 'flex',
              alignItems: 'center',
              gap: tokens.space[2],
            }}
          >
            <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18 }}>
              straighten
            </span>
            Units
          </legend>
          <div role="radiogroup" aria-label="Units" style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2] }}>
            <label style={{ display: 'flex', gap: tokens.space[2], alignItems: 'center' }}>
              <input
                type="radio"
                name="units"
                value="metric"
                checked={settings.units === 'metric'}
                onChange={() => setSettings((p) => ({ ...p, units: 'metric' }))}
              />
              Metric (meters, km, m²)
            </label>
            <label style={{ display: 'flex', gap: tokens.space[2], alignItems: 'center' }}>
              <input
                type="radio"
                name="units"
                value="imperial"
                checked={settings.units === 'imperial'}
                onChange={() => setSettings((p) => ({ ...p, units: 'imperial' }))}
              />
              Imperial (feet, miles, ft²)
            </label>
          </div>
        </fieldset>

        <div>
          <button
            type="submit"
            style={{
              padding: `${tokens.space[3]}px ${tokens.space[5]}px`,
              background: tokens.color.accent.primary,
              color: tokens.color.accent.on,
              border: 'none',
              borderRadius: tokens.radius.md,
              fontSize: tokens.font.size.md,
              fontWeight: tokens.font.weight.medium,
              cursor: 'pointer',
            }}
          >
            Save settings
          </button>
        </div>
      </form>
    </main>
  );
}
